import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData, logout } from "../../../services/apiService";
import { changeAccessCode, changePassword } from "../../../services/credentialPolicyService";
import { PASSWORD_PATTERN } from "../../../utils/credentialGateUtils";
import {
  CredentialAccessCodeRequirements,
  CredentialModalTabs,
  CredentialPasswordField,
  CredentialPasswordRequirements,
} from "./credentialChangeModalParts";
import "./ChangeCredentialsModal.scss";

const ChangeCredentialsModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const userData = getUserData();
  const [tab, setTab] = useState("password");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const [pwdForm, setPwdForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [codeForm, setCodeForm] = useState({
    oldAccessCode: "",
    newAccessCode: "",
    confirmAccessCode: "",
  });

  useEffect(() => {
    if (!open) return;
    setTab("password");
    setError("");
    setMessage("");
    setSigningOut(false);
    setPwdForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setCodeForm({ oldAccessCode: "", newAccessCode: "", confirmAccessCode: "" });
  }, [open]);

  if (!open) return null;

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    setError("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (tab === "password") {
      if (!PASSWORD_PATTERN.test(pwdForm.newPassword)) {
        setError("Please meet all password requirements before continuing.");
        return;
      }
      if (pwdForm.newPassword !== pwdForm.confirmPassword) {
        setError("New password and confirm password do not match.");
        return;
      }

      setLoading(true);
      try {
        const response = await changePassword({
          StaffIdRef: userData.StaffId,
          OrganizationIdRef: userData.OrganizationId,
          OldPassword: pwdForm.oldPassword,
          NewPassword: pwdForm.newPassword,
          ConfirmPassword: pwdForm.confirmPassword,
        });
        if (response.success !== 1) {
          setError(response.message || "Could not change password.");
          return;
        }
        setMessage(
          "Password updated. Signing you out so you can sign in with the new password.",
        );
        setPwdForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
        setSigningOut(true);
        window.setTimeout(() => logout(navigate), 900);
        return;
      } catch (err) {
        setError(err?.message || "Could not change password.");
      } finally {
        setLoading(false);
      }
      return;
    }

    const next = codeForm.newAccessCode.trim();
    const confirm = codeForm.confirmAccessCode.trim();
    if (next.length !== 6 || !/^\d{6}$/.test(next)) {
      setError("Please meet all access code requirements before continuing.");
      return;
    }
    if (next !== confirm) {
      setError("New access code and confirm access code do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await changeAccessCode({
        StaffIdRef: userData.StaffId,
        OrganizationIdRef: userData.OrganizationId,
        OldAccessCode: parseInt(codeForm.oldAccessCode, 10),
        NewAccessCode: parseInt(codeForm.newAccessCode, 10),
        ConfirmAccessCode: parseInt(codeForm.confirmAccessCode, 10),
      });
      if (response.success !== 1) {
        setError(response.message || "Could not change access code.");
        return;
      }
        setMessage(
          "Access code updated. Signing you out so you can sign in with the new access code.",
        );
        setCodeForm({ oldAccessCode: "", newAccessCode: "", confirmAccessCode: "" });
        setSigningOut(true);
        window.setTimeout(() => logout(navigate), 900);
    } catch (err) {
      setError(err?.message || "Could not change access code.");
    } finally {
      setLoading(false);
    }
  };

  const isPassword = tab === "password";

  return (
    <div
      className="modal fade show d-block credential-admin-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1200 }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Change login credentials</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div
                className="alert alert-danger py-2 px-3 small mb-3 credential-signout-warning"
                role="status"
              >
                After you update your credentials, you will be signed out of
                this session. Please sign in again using your updated
                credentials.
              </div>

              <CredentialModalTabs tab={tab} onTabChange={handleTabChange} />

              {error && (
                <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
                  {error}
                </div>
              )}
              {message && (
                <div className="alert alert-success py-2 px-3 small mb-3" role="alert">
                  {message}
                </div>
              )}

              {isPassword ? (
                <>
                  <CredentialPasswordRequirements password={pwdForm.newPassword} />
                  <CredentialPasswordField
                    id="self-old-password"
                    label="Current password"
                    value={pwdForm.oldPassword}
                    autoComplete="current-password"
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, oldPassword: e.target.value }))
                    }
                  />
                  <CredentialPasswordField
                    id="self-new-password"
                    label="New password"
                    value={pwdForm.newPassword}
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, newPassword: e.target.value }))
                    }
                  />
                  <CredentialPasswordField
                    id="self-confirm-password"
                    label="Confirm new password"
                    value={pwdForm.confirmPassword}
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, confirmPassword: e.target.value }))
                    }
                  />
                </>
              ) : (
                <>
                  <CredentialAccessCodeRequirements
                    newAccessCode={codeForm.newAccessCode}
                    confirmAccessCode={codeForm.confirmAccessCode}
                  />
                  <CredentialPasswordField
                    id="self-old-access-code"
                    label="Current access code"
                    value={codeForm.oldAccessCode}
                    maxLength={6}
                    autoComplete="off"
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        oldAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                  <CredentialPasswordField
                    id="self-new-access-code"
                    label="New access code"
                    value={codeForm.newAccessCode}
                    maxLength={6}
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        newAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                  <CredentialPasswordField
                    id="self-confirm-access-code"
                    label="Confirm new access code"
                    value={codeForm.confirmAccessCode}
                    maxLength={6}
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        confirmAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading || signingOut}
              >
                <i className="fas fa-times me-1" aria-hidden="true" />
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || signingOut}
              >
                <i className="fas fa-check me-1" aria-hidden="true" />
                {loading
                  ? "Saving…"
                  : isPassword
                    ? "Update password"
                    : "Update access code"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangeCredentialsModal;
